import { useState, useEffect, useCallback } from 'react';
import {
  getProfiles,
  getJobRequisitionView,
  getManagerEmployeesListing,
  updateEmployee,
} from 'Api/apiService';
import { SbNotification } from 'uiCore';
import useAuthStore from 'Store/useAuthStore';
import {
  MANAGER_TYPE,
  ERROR_MESSAGES,
  ERROR_TITLES,
  WARNING_TITLES,
  getApiErrorMessage,
} from 'constants/employeeManagement.constants';

const useEmployeeDrawer = () => {
  const email = useAuthStore((s) => s.email);

  const [allProfiles, setAllProfiles] = useState([]);
  const [requisitions, setRequisitions] = useState([]);
  const [managers, setManagers] = useState([]);
  const [hrbps, setHrbps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadDrawerData = useCallback(async () => {
    setLoading(true);
    try {
      const [profilesRes, requisitionsRes, managersRes, hrbpsRes] =
        await Promise.allSettled([
          getProfiles(),
          getJobRequisitionView(email),
          getManagerEmployeesListing({ managerType: MANAGER_TYPE.REPORTING_MANAGER }),
          getManagerEmployeesListing({ managerType: MANAGER_TYPE.HRBP }),
        ]);

      // Helper: check if a settled result has valid data (not an error response)
      const isValid = (res) =>
        res.status === 'fulfilled' && Array.isArray(res.value) && !res.value?.error;
      const isErrorResponse = (res) =>
        res.status === 'rejected' ||
        (res.status === 'fulfilled' && res.value?.error);

      setAllProfiles(isValid(profilesRes) ? profilesRes.value : []);
      setRequisitions(isValid(requisitionsRes) ? requisitionsRes.value : []);
      setManagers(isValid(managersRes) ? managersRes.value : []);
      setHrbps(isValid(hrbpsRes) ? hrbpsRes.value : []);

      const hasFailure = [profilesRes, requisitionsRes, managersRes, hrbpsRes].some(isErrorResponse);
      if (hasFailure) {
        SbNotification.warning(WARNING_TITLES.LOAD_EMPLOYEE_DATA, ERROR_MESSAGES.LOAD_EMPLOYEE_DATA_FAILED, { key: 'employee-drawer-warning' });
      }
    } catch (error) {
      SbNotification.warning(WARNING_TITLES.LOAD_EMPLOYEE_DATA, ERROR_MESSAGES.LOAD_EMPLOYEE_DATA_FAILED, { key: 'employee-drawer-warning' });
    } finally {
      setLoading(false);
    }
  }, [email]);

  const updateEmployeeData = useCallback(
    async ({ employeeId, payload }) => {
      setSaving(true);
      try {
        await updateEmployee({
          updatedEmployeePayload: payload,
          employeeId,
        });
        SbNotification.success('Success', 'Employee updated successfully');
        return true;
      } catch (error) {
        const msg = getApiErrorMessage(error, ERROR_MESSAGES.UPDATE_EMPLOYEE_FAILED);
        SbNotification.error(ERROR_TITLES.UPDATE_EMPLOYEE, msg);
        return false;
      } finally {
        setSaving(false);
      }
    },
    [],
  );

  // Build options for selects
  const profileOptions = allProfiles.map((p) => ({
    value: p.role_id,
    label: p.role_name,
  }));

  const requisitionOptions = requisitions.map((r) => ({
    value: r.sb_unique_id,
    label: `${r.sb_requisition_number} - ${r.job_title}`,
  }));

  const managerOptions = managers.map((m) => ({
    value: m.sb_user_unique_id,
    label: `${m.employee_id} - ${m.first_name} ${m.last_name}`,
  }));

  const hrbpOptions = hrbps.map((h) => ({
    value: h.sb_user_unique_id,
    label: `${h.employee_id} - ${h.first_name} ${h.last_name}`,
  }));

  const findManagerName = (id) => {
    const m = managers.find((mgr) => mgr.sb_user_unique_id === id);
    return m ? `${m.first_name} ${m.last_name}` : '';
  };

  const findHrbpName = (id) => {
    const h = hrbps.find((hrbp) => hrbp.sb_user_unique_id === id);
    return h ? `${h.first_name} ${h.last_name}` : '';
  };

  const findRequisitionTitle = (sbUniqueId) => {
    const r = requisitions.find((req) => req.sb_unique_id === sbUniqueId);
    return r ? r.job_title : '';
  };

  return {
    loading,
    saving,
    profileOptions,
    requisitionOptions,
    managerOptions,
    hrbpOptions,
    findManagerName,
    findHrbpName,
    findRequisitionTitle,
    loadDrawerData,
    updateEmployeeData,
  };
};

export default useEmployeeDrawer;
